import { StrictMode } from 'react'
import type * as React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { Auth0Provider } from '@auth0/auth0-react'
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
// Import the generated route tree
import { routeTree } from './routeTree.gen'
import { env } from './lib/env'
import { SSEProvider } from './contexts/SSEContext'
import { PermissionsProvider } from './contexts/PermissionsContext'
import { initFaro, logErrorToFaro } from './lib/faro'
import { getErrorMessage } from './lib/error'
import { toast } from 'sonner'
import { FaroErrorBoundary } from '@grafana/faro-react'
import { RouteErrorFallback } from './components/ErrorBoundary/ErrorFallback'

import './styles.css'
import reportWebVitals from './reportWebVitals.ts'

// Initialize Grafana Faro (only in demo/production when collector URL is configured)
initFaro()

/**
 * Centralized error logging function
 * Uses Grafana Faro when available, falls back to console in development
 */
function logError(
  error: unknown,
  errorInfo: { componentStack?: string; errorBoundary?: React.Component<unknown> },
  caught: boolean
) {
  // Convert error to Error object if needed
  const err = error instanceof Error ? error : new Error(String(error))
  
  // Log to Faro (will fallback to console if Faro not initialized)
  logErrorToFaro(err, {
    caught: String(caught),
    componentStack: errorInfo.componentStack || '',
    url: window.location.href,
  })
}

/**
 * Render function for FaroErrorBoundary fallback.
 * Uses the shared RouteErrorFallback component to avoid duplicating error UI.
 */
function renderFaroFallback(error: Error, resetError: () => void) {
  return <RouteErrorFallback error={error} reset={resetError} />
}

// Create a client
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry auth/permission errors
        if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
          return false
        }
        return failureCount < 2
      },
      refetchOnWindowFocus: false,
    },
  },
})

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {},
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: RouteErrorFallback,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    // React 19 error callbacks for centralized error logging
    onCaughtError: (error, errorInfo) => {
      logError(error, errorInfo, true)
    },
    onUncaughtError: (error, errorInfo) => {
      logError(error, errorInfo, false)
    },
  })
  root.render(
    <StrictMode>
      <FaroErrorBoundary fallback={renderFaroFallback}>
        <QueryClientProvider client={queryClient}>
          <Auth0Provider
            domain={env.AUTH0_DOMAIN}
            clientId={env.AUTH0_CLIENT_ID}
            useRefreshTokens={true}
            cacheLocation="localstorage"
            authorizationParams={{
              redirect_uri: env.AUTH0_REDIRECT_URI,
              audience: env.AUTH0_AUDIENCE,
              scope: "openid profile email organization:view organization:write user:view user:write location:view location:write calendar:view calendar:write event:view event:write phone:view phone:write event_type:view event_type:write call_log:view call_log:write"
            }}
          >
            <PermissionsProvider>
              <SSEProvider>
                <RouterProvider router={router} />
              </SSEProvider>
            </PermissionsProvider>
          </Auth0Provider>
        </QueryClientProvider>
      </FaroErrorBoundary>
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
